import Cookies from 'js-cookie';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const activeRequests = new Map();

if (!BASE_URL) {
  throw new Error('REACT_APP_API_BASE_URL environment variable is not set.');
}

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Makes authenticated API requests
 * Automatically includes JWT token from cookies
 * Handles 401/403 errors appropriately
 *
 * @param {string} path - API endpoint path (e.g., '/api/User')
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Object>} Parsed JSON response
 * @throws {ApiError} On request failure
 */
export async function apiRequest(path, options = {}) {
  const url = `${BASE_URL}${path}`;
  const token = Cookies.get('userToken');

  const headers = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  // Deduplicate GET requests
  const cacheKey = `${options.method || 'GET'}:${url}`;
  const isGet = !options.method || options.method.toUpperCase() === 'GET';

  if (isGet && activeRequests.has(cacheKey)) {
    return activeRequests.get(cacheKey);
  }

  const requestPromise = (async () => {
    try {
      const response = await fetch(url, { ...options, headers });

    // Parse response body for error details
    let errorData = null;
    let responseText = '';

    try {
      responseText = await response.text();
      if (responseText) {
        errorData = JSON.parse(responseText);
      }
    } catch (e) {
      // Response is not JSON, that's okay
    }

    // Handle 401 Unauthorized
    if (response.status === 401) {
      const error = new ApiError(
        errorData?.message || 'Unauthorized. Please login again.',
        401,
        errorData
      );

      // Clear token and redirect to login
      Cookies.remove('userToken');

      // Dispatch custom event for global auth handling
      window.dispatchEvent(new CustomEvent('authError', { detail: { status: 401 } }));

      throw error;
    }

    // Handle 403 Forbidden
    if (response.status === 403) {
      const error = new ApiError(
        'Forbidden. You do not have permission to access this resource.',
        403,
        errorData
      );
      throw error;
    }

    // Handle other HTTP errors
    if (!response.ok) {
      // Extract detailed error message from various response formats
      let errorMessage = `Request failed: ${response.status}`;

      // ASP.NET ModelState validation errors
      if (errorData && typeof errorData === 'object') {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.errors) {
          // ModelState format: { "field": ["error1", "error2"] }
          const errorDetails = Object.entries(errorData.errors)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join('; ');
          if (errorDetails) {
            errorMessage = errorDetails;
          }
        } else if (errorData.error) {
          errorMessage = errorData.error;
          // Include database-specific error if available
          if (errorData.dbError) {
            errorMessage += ` [DB: ${errorData.dbError}]`;
          }
        }
      }

      const error = new ApiError(errorMessage, response.status, errorData);
      throw error;
    }

    // Parse successful response
    if (responseText) {
      // Remove markdown code blocks if present (backend sometimes returns wrapped JSON)
      let cleanedText = responseText.trim();
      if (cleanedText.startsWith('```')) {
        // Remove opening code fence (```json, ```javascript, etc.)
        cleanedText = cleanedText.replace(/^```(?:json|javascript)?\s*\n?/, '');
        // Remove closing code fence
        cleanedText = cleanedText.replace(/\n?```$/, '');
      }

      try {
        return JSON.parse(cleanedText);
      } catch (parseError) {
        // Try to fix common JSON issues: unescaped newlines and special chars
        console.warn('Initial JSON parse failed, attempting to fix...');

        try {
          // More robust fix: replace literal newlines with escaped newlines
          let fixedText = cleanedText.replace(/\n/g, (match, offset) => {
            // Check if this newline is inside a string
            // Count unescaped quotes before this position
            const before = cleanedText.substring(0, offset);
            let quoteCount = 0;
            let escaped = false;

            for (let i = 0; i < before.length; i++) {
              if (before[i] === '\\') {
                escaped = !escaped;
              } else if (before[i] === '"' && !escaped) {
                quoteCount++;
              } else {
                escaped = false;
              }
            }

            // If odd number of quotes, we're inside a string
            if (quoteCount % 2 === 1) {
              return '\\n';
            }
            return '\n';
          });

          return JSON.parse(fixedText);
        } catch (secondError) {
          console.error('JSON Parse Error at position:', parseError.message);
          console.error('Response text (first 500 chars):', cleanedText.substring(0, 500));
          throw new ApiError(
            `Invalid JSON in response: ${parseError.message}`,
            response.status,
            { originalError: parseError }
          );
        }
      }
    }

    return {};
  } catch (error) {
    // Re-throw API errors as-is
    if (error instanceof ApiError) {
      throw error;
    }

    // Convert fetch errors to API errors
    throw new ApiError(
      error.message || 'Network request failed',
      0,
      { originalError: error }
    );
  } finally {
    if (isGet) {
      activeRequests.delete(cacheKey);
    }
  }
  })();

  if (isGet) {
    activeRequests.set(cacheKey, requestPromise);
  }

  return requestPromise;
}
